import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useParams } from 'react-router';
import type { Prompt } from '../../../types/backend';
import { usePromptService } from '../../prompt/services/promptService';

interface PromptPreviewProps {
  prompt: Prompt;
}

const PREVIEW_CHARS = 240;

const PromptPreview: React.FC<PromptPreviewProps> = ({ prompt }) => {
  const { customerId } = useParams<{ customerId: string }>();
  const promptService = usePromptService();
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const isCustomerScoped = prompt.owner === 'customer';
  const canFetch = !!prompt.promptVersion && (!isCustomerScoped || !!customerId);

  useEffect(() => {
    if (!canFetch) return;
    let cancelled = false;
    setLoading(true);
    setContent(null);
    // For customer prompts, pass the route customerId. For global, customerId is ignored by the service.
    promptService
      .getPromptVersion(prompt.id, prompt.promptVersion!, prompt.owner, isCustomerScoped ? customerId : undefined)
      .then((result) => {
        if (!cancelled) setContent(result.content ?? null);
      })
      .catch(() => {
        // content unavailable — show metadata only
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.id, prompt.promptVersion, prompt.owner, customerId, canFetch]);

  const isTruncated = content ? content.length > PREVIEW_CHARS : false;
  const displayContent = content
    ? showFull ? content : content.slice(0, PREVIEW_CHARS) + (isTruncated ? '…' : '')
    : null;

  return (
    <Box
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {prompt.title || prompt.name}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {prompt.type}
        </Typography>
        {loading && <CircularProgress size={10} />}
      </Box>

      {prompt.description && (
        <Typography variant="body2" color="text.secondary">
          {prompt.description}
        </Typography>
      )}

      {displayContent && (
        <>
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '0.72rem', whiteSpace: 'pre-wrap', color: 'text.secondary' }}
          >
            {displayContent}
          </Typography>
          {isTruncated && (
            <Box
              component="button"
              onClick={() => setShowFull((v) => !v)}
              sx={{
                mt: 0.25,
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: 'text.secondary',
                fontSize: '0.72rem',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {showFull ? 'Show less' : 'Show more'}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PromptPreview;
